// src/pages/InvoiceManagement.js - billing details, choreographies to pay, invoice history.
//
// Audit fixes (leader side of INV-11, INV-16, INV-27, INV-28, INV-29, INV-34, FL-02, FL-15):
// * money always in the invoice/price currency via formatMoney - EUR never labelled "Kč";
// * prices rounded to cents; unit price × dancers shown;
// * confirmed AND approved choreographies can be invoiced; the backend's reason is shown
//   for the others; items grouped by competition;
// * the backend's error (400/409/502) is shown in the confirmation dialog, success is
//   confirmed with the invoice number;
// * invoice status via StatusBadge incl. "Stornováno"; payment box with the variable
//   symbol, bank details for the currency and the amount; overdue invoices highlighted;
// * billing form with visible borders, labels, limits and Czech validation messages;
// * a natural person sends no IČO / DIČ (an IČO typed before ticking the box is dropped);
// * each competition of "K úhradě" says the payment deadline (a week before it).
import React, { useCallback, useMemo, useState } from 'react';
import { AlertTriangle, CreditCard, FileText, Pencil, Receipt } from 'lucide-react';
import api, { getErrorMessage, toApiError } from '../services/api';
import useFetch from '../hooks/useFetch';
import { formatDate, formatMoney, countLabel, daysUntil, isPastCompetition, presenceDeadline } from '../utils/format';
import { leagueLabel } from '../constants/categories';
import { Alert, Button, ConfirmDialog, EmptyState, PageHeader, Spinner, StatusBadge, useToast } from '../components/ui';
import DownloadButton from '../components/DownloadButton';
import TextField from '../components/leader/TextField';
import LoadError from '../components/leader/LoadError';

const COUNTRIES = { 2: 'Česká republika', 1: 'Slovenská republika' };
const currencyForCountry = (countryId) => (Number(countryId) === 1 ? 'EUR' : 'CZK');
const INVOICEABLE = ['confirmed', 'approved'];
const CHOREO_FORMS = ['choreografii', 'choreografie', 'choreografií'];
const EMPTY_BILLING = {
  company_name: '',
  street: '',
  city: '',
  postal_code: '',
  identification_number: '',
  vat_identification_number: '',
  country_id: 2,
  is_natural_person: false,
};
const TIER_LABELS = { early_bird: 'Zvýhodněné startovné', free: 'Zdarma' };

const isInvoiceable = (item) => (typeof item.invoiceable === 'boolean' ? item.invoiceable : INVOICEABLE.includes(item.status));
const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

function BillingInfoForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(() => ({ ...EMPTY_BILLING, ...(initial || {}) }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const naturalPerson = Boolean(form.is_natural_person);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form, country_id: Number(form.country_id), is_natural_person: naturalPerson };
      if (naturalPerson) {
        payload.identification_number = '';
        payload.vat_identification_number = '';
      }
      await onSave(payload);
    } catch (err) {
      const apiError = toApiError(err);
      setError({ message: apiError.message, details: Array.isArray(apiError.details) ? apiError.details : null });
    } finally {
      setSaving(false);
    }
  };

  const details = error && error.details && error.details.length > 1 ? error.details : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {error && <Alert tone="error" title={error.message} details={details} />}
      <label className="flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
        <input
          type="checkbox"
          checked={naturalPerson}
          onChange={(e) => setForm({ ...form, is_natural_person: e.target.checked })}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
        <span>
          <span className="font-medium">Přihlašuji se jako fyzická osoba</span>
          <span className="block text-gray-600">Skupina nemá IČO – faktura se vystaví na vaše jméno.</span>
        </span>
      </label>
      <TextField
        label={naturalPerson ? 'Jméno a příjmení' : 'Název firmy / spolku nebo jméno a příjmení'}
        value={form.company_name}
        onChange={set('company_name')}
        maxLength={200}
        autoComplete={naturalPerson ? 'name' : 'organization'}
        required
      />
      <TextField as="select" label="Země" value={form.country_id} onChange={set('country_id')} required>
        <option value={2}>Česká republika (platba v Kč)</option>
        <option value={1}>Slovenská republika (platba v eurech)</option>
      </TextField>
      <TextField
        label="Ulice a číslo"
        value={form.street}
        onChange={set('street')}
        maxLength={100}
        autoComplete="street-address"
        required
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField label="Město" value={form.city} onChange={set('city')} maxLength={50} autoComplete="address-level2" required />
        <TextField
          label="PSČ"
          value={form.postal_code}
          onChange={set('postal_code')}
          maxLength={11}
          autoComplete="postal-code"
          inputMode="numeric"
          required
        />
      </div>
      {!naturalPerson && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="IČO"
            value={form.identification_number || ''}
            onChange={set('identification_number')}
            maxLength={20}
            inputMode="numeric"
            required
          />
          <TextField
            label={Number(form.country_id) === 1 ? 'IČ DPH / DIČ (nepovinné)' : 'DIČ (nepovinné)'}
            value={form.vat_identification_number || ''}
            onChange={set('vat_identification_number')}
            maxLength={20}
          />
        </div>
      )}
      <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
        {onCancel && (
          <Button variant="secondary" onClick={onCancel} disabled={saving}>
            Zrušit
          </Button>
        )}
        <Button type="submit" loading={saving}>
          Uložit fakturační údaje
        </Button>
      </div>
    </form>
  );
}

/** "Uhraďte nejpozději do 3. 4. 2027" (a week before the competition), amber in the last week. */
function PaymentDeadline({ competition }) {
  const due = isPastCompetition(competition) ? null : presenceDeadline(competition);
  if (!due) return null;
  const days = daysUntil(due);
  const urgent = days !== null && days <= 7;
  return (
    <p className={`text-sm ${urgent ? 'font-medium text-amber-800' : 'text-gray-600'}`}>
      Uhraďte nejpozději do {formatDate(due)}
    </p>
  );
}

function PaymentInfo({ invoice }) {
  const isEur = invoice.currency === 'EUR';
  const rows = [
    !isEur && invoice.bank_account && ['Číslo účtu', invoice.bank_account],
    invoice.bank_name && ['Banka', invoice.bank_name],
    isEur && invoice.iban && ['IBAN', invoice.iban],
    isEur && invoice.swift && ['SWIFT', invoice.swift],
    ['Variabilní symbol', invoice.variable_symbol || '—'],
    ['Částka', formatMoney(invoice.total_amount, invoice.currency)],
  ].filter(Boolean);
  return (
    <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-4">
      <p className="font-medium text-amber-900">Informace k platbě</p>
      <dl className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {rows.map(([term, value]) => (
          <div key={term}>
            <dt className="text-sm text-gray-600">{term}</dt>
            <dd className="font-medium text-gray-900 break-all">{value}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 text-sm text-gray-700">
        Při platbě prosím uveďte variabilní symbol, aby šlo platbu spárovat. Fakturu v PDF si můžete kdykoli stáhnout.
      </p>
    </div>
  );
}

function InvoiceCard({ invoice }) {
  const overdue = invoice.status === 'issued' && (daysUntil(invoice.due_date) ?? 0) < 0;
  return (
    <article className={`rounded-lg border p-4 ${overdue ? 'border-red-200' : 'border-gray-200'}`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="font-medium text-gray-900">Faktura č. {invoice.number}</h3>
          <p className="text-sm text-gray-600">Vystaveno {formatDate(invoice.issued_date)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge kind="invoice" status={invoice.status} />
          {overdue && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
              Po splatnosti
            </span>
          )}
        </div>
      </div>

      <ul className="mt-3 space-y-1 text-sm">
        {(invoice.items || []).map((item, idx) => (
          <li key={item.choreography_id || idx} className="flex justify-between gap-3">
            <span className="text-gray-700">
              {item.choreography_name || 'Choreografie'}
              {item.quantity && item.unit_price ? (
                <span className="text-gray-600">
                  {' '}
                  ({item.quantity} × {formatMoney(item.unit_price, invoice.currency)})
                </span>
              ) : null}
            </span>
            <span className="whitespace-nowrap text-gray-900">{formatMoney(item.amount, invoice.currency)}</span>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-gray-700">
          {invoice.status === 'paid' && invoice.paid_date && <p>Zaplaceno {formatDate(invoice.paid_date)}</p>}
          {invoice.status === 'issued' && <p className={overdue ? 'font-medium text-red-700' : ''}>Splatnost {formatDate(invoice.due_date)}</p>}
          {invoice.status === 'cancelled' && (
            <p>Faktura byla stornována{invoice.cancelled_at ? ` ${formatDate(invoice.cancelled_at)}` : ''}. Neplaťte ji.</p>
          )}
        </div>
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
          <p className="text-lg font-semibold text-gray-900">Celkem {formatMoney(invoice.total_amount, invoice.currency)}</p>
          <DownloadButton invoiceId={invoice.id} invoiceNumber={invoice.number} label="Stáhnout PDF" />
        </div>
      </div>

      {invoice.status === 'issued' && <PaymentInfo invoice={invoice} />}
    </article>
  );
}

const InvoiceManagement = () => {
  const toast = useToast();
  const { data, error, isLoading, reload } = useFetch(async () => {
    const [billingRes, pendingRes, invoicesRes] = await Promise.all([
      api.get('/invoices/billing-info'),
      api.get('/invoices/pending-choreographies'),
      api.get('/invoices/'),
    ]);
    return { billing: billingRes.data, pending: pendingRes.data || [], invoices: invoicesRes.data || [] };
  }, 'all');

  const [isEditingBilling, setIsEditingBilling] = useState(false);
  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [created, setCreated] = useState(null);

  const billing = data?.billing;
  const pending = useMemo(() => data?.pending || [], [data]);
  const invoices = data?.invoices || [];
  const fallbackCurrency = currencyForCountry(billing?.details?.country_id);

  const groups = useMemo(() => {
    const map = new Map();
    pending.forEach((item) => {
      const key = item.competition?.id ?? item.competition?.name ?? 'none';
      if (!map.has(key)) map.set(key, { competition: item.competition || {}, items: [] });
      map.get(key).items.push(item);
    });
    return Array.from(map.values());
  }, [pending]);

  const selectedItems = pending.filter((item) => selected.includes(item.id));
  const totals = selectedItems.reduce((acc, item) => {
    const currency = item.currency || fallbackCurrency;
    acc[currency] = round2((acc[currency] || 0) + (Number(item.price) || 0));
    return acc;
  }, {});
  const totalText = Object.entries(totals)
    .map(([currency, amount]) => formatMoney(amount, currency))
    .join(' + ');

  const closeConfirm = useCallback(() => {
    setConfirmOpen(false);
    setCreateError(null);
  }, []);

  const toggle = (id, checked) => {
    setSelected((list) => (checked ? [...list, id] : list.filter((x) => x !== id)));
  };

  const selectAllInvoiceable = () => setSelected(pending.filter(isInvoiceable).map((item) => item.id));

  const handleSaveBilling = async (form) => {
    await api.put('/invoices/billing-info', form);
    setIsEditingBilling(false);
    toast.success('Fakturační údaje byly uloženy.');
    await reload({ silent: true });
  };

  const handleCreateInvoice = async () => {
    if (creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      const response = await api.post('/invoices/create', { choreography_ids: selected });
      const invoice = response.data || {};
      setConfirmOpen(false);
      setSelected([]);
      setCreated(invoice);
      toast.success(invoice.number ? `Faktura č. ${invoice.number} byla vytvořena.` : 'Faktura byla vytvořena.');
      await reload({ silent: true });
    } catch (err) {
      setCreateError(getErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  if (isLoading && !data) return <Spinner label="Načítám fakturaci…" />;
  if (error && !data) return <LoadError title="Fakturaci se nepodařilo načíst" error={error} onRetry={reload} />;

  const invoiceableCount = pending.filter(isInvoiceable).length;

  return (
    <div className="space-y-6">
      <PageHeader title="Fakturace" subtitle="Startovné se platí na základě faktury, kterou si zde vystavíte." />

      {created && (
        <Alert tone="success" title={created.number ? `Faktura č. ${created.number} je vystavená` : 'Faktura je vystavená'} onClose={() => setCreated(null)}>
          {created.email_sent === false
            ? 'E-mail s fakturou se nepodařilo odeslat – stáhněte si ji níže v historii faktur.'
            : 'Fakturu jsme poslali e-mailem a najdete ji níže v historii faktur včetně platebních údajů.'}
        </Alert>
      )}

      <section className="rounded-lg bg-white p-4 shadow sm:p-6" aria-labelledby="billing-title">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 id="billing-title" className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <FileText className="h-5 w-5 text-indigo-600" aria-hidden="true" />
            Fakturační údaje
          </h2>
          {billing?.exists && !isEditingBilling && (
            <Button variant="ghost" onClick={() => setIsEditingBilling(true)}>
              <Pencil className="h-4 w-4" aria-hidden="true" />
              Upravit
            </Button>
          )}
        </div>

        {isEditingBilling || !billing?.exists ? (
          <>
            {!billing?.exists && (
              <p className="mb-4 text-sm text-gray-600">
                Než si vystavíte první fakturu, vyplňte prosím fakturační údaje. Podle země se určí měna faktury.
              </p>
            )}
            <BillingInfoForm
              initial={billing?.details}
              onSave={handleSaveBilling}
              onCancel={billing?.exists ? () => setIsEditingBilling(false) : null}
            />
          </>
        ) : (
          <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
            {[
              [billing.details.is_natural_person ? 'Jméno (fyzická osoba)' : 'Název firmy / jméno', billing.details.company_name],
              ['Země', COUNTRIES[billing.details.country_id] || '—'],
              ['Ulice', billing.details.street],
              ['Město', billing.details.city],
              ['PSČ', billing.details.postal_code],
              !billing.details.is_natural_person && ['IČO', billing.details.identification_number || '—'],
              billing.details.vat_identification_number && ['DIČ', billing.details.vat_identification_number],
            ]
              .filter(Boolean)
              .map(([term, value]) => (
                <div key={term}>
                  <dt className="text-gray-600">{term}</dt>
                  <dd className="font-medium text-gray-900">{value}</dd>
                </div>
              ))}
          </dl>
        )}
      </section>

      {billing?.exists && (
        <section className="rounded-lg bg-white p-4 shadow sm:p-6" aria-labelledby="pending-title">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 id="pending-title" className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <CreditCard className="h-5 w-5 text-indigo-600" aria-hidden="true" />
              K úhradě
            </h2>
            {invoiceableCount > 1 && (
              <Button variant="ghost" onClick={selectAllInvoiceable}>
                Vybrat vše k fakturaci ({invoiceableCount})
              </Button>
            )}
          </div>

          {pending.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-600">Nemáte žádné choreografie k úhradě.</p>
          ) : (
            <>
              <p className="mb-4 text-sm text-gray-600">
                Startovné = počet tanečníků × cena za tanečníka. Fakturovat lze choreografie s potvrzenou prezencí; na jednu
                fakturu můžete dát choreografie z více soutěží.
              </p>
              <div className="space-y-5">
                {groups.map((group) => (
                  <div key={group.competition.id ?? group.competition.name ?? 'none'}>
                    <div className="mb-2">
                      <h3 className="text-sm font-semibold text-gray-700">
                        {group.competition.name}
                        {group.competition.date && ` · ${formatDate(group.competition.date)}`}
                      </h3>
                      <PaymentDeadline competition={group.competition} />
                    </div>
                    <ul className="space-y-2">
                      {group.items.map((item) => {
                        const invoiceable = isInvoiceable(item);
                        const currency = item.currency || fallbackCurrency;
                        const checked = selected.includes(item.id);
                        return (
                          <li key={item.id}>
                            <label
                              className={`flex items-start justify-between gap-3 rounded-lg border p-3 ${
                                invoiceable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed bg-gray-50'
                              } ${checked ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}
                            >
                              <span className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={!invoiceable}
                                  onChange={(e) => toggle(item.id, e.target.checked)}
                                  className="mt-0.5 h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <span>
                                  <span className="block font-medium text-gray-900">{item.name}</span>
                                  <span className="block text-sm text-gray-600">
                                    {leagueLabel(item.league)} · {countLabel(item.dancer_count || 0, ['tanečník', 'tanečníci', 'tanečníků'])}
                                  </span>
                                  {!invoiceable && (
                                    <span className="mt-1 block text-sm text-amber-800">
                                      {item.reason || 'Nejdřív potvrďte prezenci choreografie.'}
                                    </span>
                                  )}
                                </span>
                              </span>
                              <span className="text-right">
                                <span className="block whitespace-nowrap font-medium text-gray-900">
                                  {formatMoney(round2(item.price), currency)}
                                </span>
                                {item.unitPrice !== undefined && item.unitPrice !== null && (
                                  <span className="block whitespace-nowrap text-xs text-gray-600">
                                    {item.dancer_count || 0} × {formatMoney(item.unitPrice, currency)}
                                  </span>
                                )}
                                {TIER_LABELS[item.tier] && (
                                  <span className="block text-xs text-green-700">{TIER_LABELS[item.tier]}</span>
                                )}
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-base font-medium text-gray-900">
                  {selectedItems.length > 0
                    ? `Vybráno ${countLabel(selectedItems.length, CHOREO_FORMS)} · celkem ${totalText}`
                    : 'Vyberte choreografie, za které chcete vystavit fakturu.'}
                </p>
                <Button onClick={() => setConfirmOpen(true)} disabled={selectedItems.length === 0 || creating}>
                  <Receipt className="h-4 w-4" aria-hidden="true" />
                  Vytvořit fakturu
                </Button>
              </div>
            </>
          )}
        </section>
      )}

      <section className="rounded-lg bg-white p-4 shadow sm:p-6" aria-labelledby="history-title">
        <h2 id="history-title" className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
          <FileText className="h-5 w-5 text-indigo-600" aria-hidden="true" />
          Historie faktur
        </h2>
        {invoices.length === 0 ? (
          <EmptyState title="Zatím nemáte žádné faktury" />
        ) : (
          <div className="space-y-4">
            {invoices.map((invoice) => (
              <InvoiceCard key={invoice.id} invoice={invoice} />
            ))}
          </div>
        )}
      </section>

      {confirmOpen && (
        <ConfirmDialog
          title="Potvrdit vytvoření faktury"
          confirmLabel="Vytvořit fakturu"
          loading={creating}
          error={createError}
          onConfirm={handleCreateInvoice}
          onCancel={closeConfirm}
        >
          <p>
            Vystavíme fakturu na částku <strong>{totalText}</strong> za {countLabel(selectedItems.length, CHOREO_FORMS)}.
          </p>
          <p>Fakturu vám pošleme e-mailem a najdete ji i zde v historii faktur.</p>
        </ConfirmDialog>
      )}
    </div>
  );
};

export default InvoiceManagement;
